/**
 * UploadAudioSeries.jsx — June 2 2026.
 *
 * Pill 2 of the upload hub. Two responsibilities:
 *   1. List the scribe's existing audio series with an inline Edit form.
 *   2. Create a new series (cover, title, description, author, genre).
 *
 * Series fields are editable at any time (no edit-count limit). Per-
 * episode uploads + the 2-edit cap live on /upload-episodes.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Headphones, Loader2, Plus, Pencil, Save, X, ImageIcon, Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { authFetch, checkAuth } from '../utils/authUtils';
import { resolveCoverUrl } from '../utils/coverUrl';
import CoverImagePicker from '../components/CoverImagePicker';
import OriginalContentAck from '../components/OriginalContentAck';
import { recordOriginalAck } from '../utils/originalAck';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const GENRES = [
  'Fiction', 'Non-Fiction', 'Science Fiction', 'Fantasy', 'Mystery',
  'Thriller', 'Romance', 'Poetry', 'Biography', 'History', 'Science',
  'Self-Help', 'Business', 'Technology', 'Education', 'Other',
];

const UploadAudioSeries = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('list'); // list | create | edit
  const [editing, setEditing] = useState(null); // series doc when editing

  const refresh = async () => {
    const r = await authFetch(`${BACKEND_URL}/api/library`);
    if (!r.ok) return;
    const d = await r.json();
    // Feb 12 2026 — High King and Kings see every scribe's series so
    // they can edit on the scribe's behalf. Writers still see only
    // their own.
    const all = d.series || [];
    const role = me?.role;
    if (role === 'admin' || role === 'coadmin' || role === 'king') {
      setSeries(all);
    } else {
      setSeries(all.filter((s) => s.user_id === me?.user_id));
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const u = await checkAuth();
      if (!u) { navigate('/login'); return; }
      if (u.role !== 'writer' && u.role !== 'admin' && u.role !== 'coadmin' && u.role !== 'king') {
        navigate('/upload');
        return;
      }
      if (!cancelled) setMe(u);
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  useEffect(() => {
    if (!me) return;
    (async () => { await refresh(); setLoading(false); })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  if (loading || !me) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-6">
        <p className="text-slate-400 text-sm inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </p>
      </main>
    );
  }

  return (
    <main className="max-w-3xl mx-auto px-4 py-6 pb-32" data-testid="upload-audio-series-page">
      <div className="flex items-center justify-between mb-5">
        <button
          type="button"
          onClick={() => navigate('/upload')}
          className="text-slate-400 hover:text-white inline-flex items-center gap-1 text-sm"
          data-testid="upload-audio-series-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center gap-2">
          <Headphones className="w-4 h-4 text-fuchsia-300" />
          <h1 className="text-lg font-bold text-white">Audio Series</h1>
        </div>
        {mode === 'list' ? (
          <Button
            size="sm"
            onClick={() => { setMode('create'); setEditing(null); }}
            className="bg-fuchsia-500 hover:bg-fuchsia-400 text-white"
            data-testid="upload-audio-series-new-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> New
          </Button>
        ) : (
          <button
            type="button"
            onClick={() => { setMode('list'); setEditing(null); }}
            className="text-slate-400 hover:text-white inline-flex items-center gap-1 text-sm"
            data-testid="upload-audio-series-cancel"
          >
            <X className="w-4 h-4" /> Cancel
          </button>
        )}
      </div>

      {mode === 'list' && (
        <SeriesList
          series={series}
          onEdit={(s) => { setMode('edit'); setEditing(s); }}
        />
      )}
      {(mode === 'create' || mode === 'edit') && (
        <SeriesForm
          initial={mode === 'edit' ? editing : null}
          onCancel={() => { setMode('list'); setEditing(null); }}
          onSaved={async () => {
            await refresh();
            setMode('list'); setEditing(null);
          }}
        />
      )}
    </main>
  );
};

// ── List rows ─────────────────────────────────────────────────────
const SeriesList = ({ series, onEdit }) => {
  if (series.length === 0) {
    return (
      <p className="text-sm text-slate-500 text-center py-10" data-testid="audio-series-empty">
        No audio series yet. Tap <span className="text-fuchsia-300 font-bold">New</span> to create one.
      </p>
    );
  }
  return (
    <div className="space-y-2" data-testid="audio-series-list">
      {series.map((s) => {
        const cover = s.cover_image || s.cover_image_url;
        const coverUrl = cover ? resolveCoverUrl(cover) : null;
        return (
          <div
            key={s.series_id || s.book_id}
            className="flex items-center gap-3 p-3.5 rounded-xl bg-slate-800/60 border border-slate-700"
            data-testid={`audio-series-row-${s.series_id || s.book_id}`}
          >
            <div className="w-12 h-12 rounded-lg bg-slate-900/60 border border-slate-700 overflow-hidden flex items-center justify-center flex-shrink-0">
              {coverUrl ? <img src={coverUrl} alt="" className="w-full h-full object-cover" /> : <Headphones className="w-5 h-5 text-slate-300" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-bold text-sm truncate">{s.title || 'Untitled'}</p>
              <p className="text-[11px] text-slate-400 truncate">
                {s.author ? `by ${s.author}` : 'No author'} · {s.chapter_count || s.episode_count || (s.episodes?.length) || 0} episodes
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onEdit(s)}
              className="border-slate-700 text-slate-200 hover:bg-slate-800 flex-shrink-0"
              data-testid={`audio-series-edit-${s.series_id || s.book_id}`}
            >
              <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
            </Button>
          </div>
        );
      })}
    </div>
  );
};

// ── Create / Edit form ────────────────────────────────────────────
const SeriesForm = ({ initial, onCancel, onSaved }) => {
  const navigate = useNavigate();
  const [coverUrl, setCoverUrl] = useState(initial?.cover_image || initial?.cover_image_url || null);
  const [title, setTitle] = useState(initial?.title || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [author, setAuthor] = useState(initial?.author || '');
  const [genre, setGenre] = useState(initial?.genre || '');
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  // Originality acknowledgement — only required when creating a NEW series.
  const [originalAck, setOriginalAck] = useState(false);

  const isEdit = !!initial;
  const canSave = title.trim() && author.trim() && genre.trim() && (isEdit || originalAck);

  const generateCover = async () => {
    if (!title.trim()) {
      toast.error('Add a title first so the AI has a prompt.');
      return;
    }
    setAiBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/ai/cover/generate`, {
        method: 'POST',
        body: JSON.stringify({
          title: title.trim(),
          author: author.trim() || 'Unknown',
          description: description.trim() || '',
          content_type: 'audiobook',
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.cover_url) {
        setCoverUrl(d.cover_url);
        toast.success('Cover generated.');
      } else {
        toast.error(d.detail || 'Cover generation failed.');
      }
    } catch {
      toast.error('Network error generating cover.');
    } finally {
      setAiBusy(false);
    }
  };

  const handleSave = async () => {
    if (!canSave) {
      toast.error('Title, author, and genre are required.');
      return;
    }
    setSaving(true);
    try {
      let res;
      if (isEdit) {
        res = await authFetch(`${BACKEND_URL}/api/audiobook/series/${initial.series_id}`, {
          method: 'PUT',
          body: JSON.stringify({
            title: title.trim(),
            description: description.trim(),
            author: author.trim(),
            genre,
            cover_image: coverUrl || null,
          }),
        });
      } else {
        res = await authFetch(`${BACKEND_URL}/api/audiobook/series/create`, {
          method: 'POST',
          body: JSON.stringify({
            title: title.trim(),
            description: description.trim(),
            author: author.trim(),
            genre,
            cover_image_url: coverUrl || null,
          }),
        });
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || 'Save failed.');
        return;
      }
      if (isEdit) {
        toast.success('Series updated.');
        onSaved?.();
      } else {
        // Fresh series → jump straight into Episode 1 upload (Jun 2 2026 user spec).
        toast.success('Series created — let\u2019s add Episode 1.');
        const newId = data.series_id;
        if (newId) {
          // Stamp originality ack on the new series before navigating.
          recordOriginalAck(newId).catch(() => {});
          navigate(`/upload-episodes?series_id=${encodeURIComponent(newId)}`);
        } else {
          onSaved?.();
        }
      }
    } catch {
      toast.error('Network error saving series.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 sm:p-6 space-y-5" data-testid="audio-series-form">
      <header>
        <h2 className="text-lg font-semibold">
          {isEdit ? 'Edit series' : 'Create series'}
        </h2>
        <p className="text-xs text-slate-500 mt-1">
          You can come back and edit any of these fields at any time.
        </p>
      </header>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-slate-200 inline-flex items-center gap-1">
            <ImageIcon className="w-3.5 h-3.5" /> Cover
          </label>
          <Button
            size="sm"
            variant="outline"
            onClick={generateCover}
            disabled={aiBusy}
            className="border-fuchsia-500/40 text-fuchsia-200 hover:bg-fuchsia-500/10"
            data-testid="audio-series-ai-cover-btn"
          >
            {aiBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
            Generate cover
          </Button>
        </div>
        <CoverImagePicker value={coverUrl} onChange={setCoverUrl} label="" />
      </div>

      <div>
        <label className="text-sm font-medium text-slate-200 mb-1 block">Series title</label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. The Night Wanderer"
          className="bg-slate-950 border-slate-700 text-white"
          data-testid="audio-series-title-input"
        />
      </div>
      <div>
        <label className="text-sm font-medium text-slate-200 mb-1 block">Description</label>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          placeholder="What's this series about?"
          className="bg-slate-950 border-slate-700 text-white"
          data-testid="audio-series-description-input"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-slate-200 mb-1 block">Author</label>
          <Input
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            placeholder="Your pen name"
            className="bg-slate-950 border-slate-700 text-white"
            data-testid="audio-series-author-input"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-200 mb-1 block">Genre</label>
          <select
            value={genre}
            onChange={(e) => setGenre(e.target.value)}
            className="w-full bg-slate-950 border border-slate-700 text-white rounded-md px-3 py-2 text-sm"
            data-testid="audio-series-genre-select"
          >
            <option value="">Select genre…</option>
            {GENRES.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
      </div>

      {!isEdit && (
        <OriginalContentAck
          checked={originalAck}
          onChange={setOriginalAck}
          testId="audio-series-original-ack"
        />
      )}

      <div className="flex gap-2 pt-2">
        <Button
          onClick={handleSave}
          disabled={!canSave || saving}
          className="flex-1 bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-semibold"
          data-testid="audio-series-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {isEdit ? 'Save changes' : 'Create series'}
        </Button>
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={saving}
          className="flex-1 border-slate-700 text-slate-200 hover:bg-slate-800"
          data-testid="audio-series-cancel-btn"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default UploadAudioSeries;
